/**
 * RemovableTagChipView — one removable tag chip on a player card.
 * @module components/organisms/custom/ui/RemovableTagChip/RemovableTagChipView
 */

import React, { memo } from "react";

import RemovablePill from "@/components/molecules/RemovablePill";

/** Props for {@link RemovableTagChipView}. */
export interface RemovableTagChipViewProps {
  /** The tag's display name — the chip's label and its identity. */
  readonly name: string;
  /** Removal handler, already bound to this tag by the Container. */
  readonly onRemove: () => void;
  /** Suppresses a drag starting from a tag interaction. */
  readonly onPreventDrag?: () => void;
}

/**
 * Renders one removable tag chip.
 * @param root0 - Chip props
 * @param root0.name - The tag's display name
 * @param root0.onRemove - Removal handler, bound to this tag
 * @param root0.onPreventDrag - Drag suppression on tag interaction
 * @returns The chip.
 */
const RemovableTagChipView = ({
  name,
  onRemove,
  onPreventDrag,
}: RemovableTagChipViewProps) => (
  <RemovablePill
    label={name}
    onRemove={onRemove}
    onPress={onPreventDrag}
    removeAccessibilityLabel={`Remove ${name}`}
  />
);

RemovableTagChipView.displayName = "RemovableTagChipView";

export default memo(RemovableTagChipView);
